import { clsx } from 'clsx';
import type { DateRangePickerRenderIconFunction } from './types';

import { Icon } from '@zapier/zinnia-icons';
import { Input, type InputProps } from '../../forms/Input';
import { DateRangePickerValue } from './DateRangePickerValue';
import { DateOption, DateRange } from './types';

import styles from './DateRangePickerInput.module.scss';

type Props = {
  /** Callback to clear the selected value */
  clearSelection: () => void;
  /** HTML `id` attribute for the element, used to associate it with a `label`. */
  id?: string;
  /** Disables the input and makes it non-interactive. */
  isDisabled: boolean;
  /** Indicates the field has an error */
  isErrored: boolean;
  /** Is menu open */
  isOpen: boolean;
  /** Callback for when the input is clicked. */
  onClick?: () => void;
  /** an optional list of predefined DateRanges the user can select from */
  options: Array<DateOption>;
  /** A hint to the user of what can be entered in the control. */
  placeholder?: string;
  /** value of the input */
  value: DateRange;
  /** rendered size of the input. */
  size?: InputProps['size'];
  /**
   * Optional custom render for the icon shown before the input value.
   * Return `null` to render nothing. When undefined, a calendar icon is shown.
   */
  renderIconBefore?: DateRangePickerRenderIconFunction;
  /**
   * Optional custom render for the icon shown after the input value.
   * Return `null` to render nothing. When undefined, the clear button is shown when a value exists.
   */
  renderIconAfter?: DateRangePickerRenderIconFunction;
};

/**
 * The icon which sits inside the input field.
 */
const ClearSelectionButton = ({
  clearSelection,
}: {
  clearSelection: () => void;
  isFocused: boolean;
  isHovered: boolean;
}) => {
  return (
    <button
      type="button"
      className={styles.clearSelectionButton}
      onClick={clearSelection}
      title="Clear Selection"
      data-zds
    >
      <Icon canAcceptPointerEvents={false} name="formXCircle" size={20} />
    </button>
  );
};

export const DateRangePickerInput = (props: Props) => {
  const { value, clearSelection, size } = props;
  const { from, to } = value;
  const hasValue = Boolean(from || to);
  const showClearSelectionButton = hasValue && !props.isDisabled;

  return (
    <div
      className={styles.root}
      data-disabled={props.isDisabled ? 'true' : undefined}
      data-zds
    >
      <Input
        id={props.id}
        isDisabled={props.isDisabled}
        isErrored={props.isErrored}
        renderIconAfter={({ iconSize, isFocused, isHovered }) => {
          if (props.renderIconAfter) {
            return props.renderIconAfter({
              iconSize,
              isFocused,
              isHovered,
              hasValue,
              isDisabled: props.isDisabled,
              clearSelection,
            });
          }
          return showClearSelectionButton ? (
            <ClearSelectionButton
              clearSelection={clearSelection}
              isFocused={isFocused}
              isHovered={isHovered}
            />
          ) : null;
        }}
        renderIconBefore={({ iconSize, isFocused, isHovered }) => {
          if (props.renderIconBefore) {
            return props.renderIconBefore({
              iconSize,
              isFocused,
              isHovered,
              hasValue,
              clearSelection,
              isDisabled: props.isDisabled,
            });
          }
          return <Icon name="actionCalendar" size={iconSize} />;
        }}
        size={size}
      >
        {(inputProps: any) => (
          // Intentionally `button` and not `Button` since it looks
          // and behaves differently.
          // eslint-disable-next-line jsx-a11y/role-supports-aria-props
          <button
            aria-controls={inputProps['aria-controls']}
            aria-expanded={props.isOpen ? 'true' : 'false'}
            aria-haspopup="true"
            aria-invalid={inputProps['aria-invalid']}
            aria-label={inputProps['aria-label']}
            data-size={size}
            className={clsx(styles.button, inputProps.className)}
            disabled={inputProps.disabled}
            id={inputProps.id}
            onClick={props.onClick}
            onKeyDown={inputProps.onKeyDown}
            type="button"
            data-zds
          >
            {hasValue ? (
              <DateRangePickerValue
                options={props.options}
                value={props.value}
              />
            ) : (
              <span className={styles.placeholder} data-zds>
                {props.placeholder}
              </span>
            )}
          </button>
        )}
      </Input>
    </div>
  );
};
